import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import { tokens } from "../../theme";
import LineChart from "../../components/LineChart";
import StatBox from "../../components/StatBox";
import { useEffect, useState, useRef } from "react";

// ✅ استيراد أيقونات متنوعة وفاخرة لكل عيار
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import StarsOutlinedIcon from "@mui/icons-material/StarsOutlined";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import DiamondOutlinedIcon from "@mui/icons-material/DiamondOutlined";
import ScaleOutlinedIcon from "@mui/icons-material/ScaleOutlined";

const DAR_AL_SABAEK_API =
  "https://api.daralsabaek.com/api/goldAndFundBalance/getMetalPrices";

const GoldPriceSystem = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobileOrTablet = useMediaQuery("(max-width:1024px)");
  const tradingViewContainer = useRef(null);

  const performUpdateRef = useRef(null);

  const [prices, setPrices] = useState({
    "24K": { current: 0, previous: 0, label: "ذهب عيار 24" },
    "22K": { current: 0, previous: 0, label: "ذهب عيار 22" },
    "21K": { current: 0, previous: 0, label: "ذهب عيار 21" },
    "18K": { current: 0, previous: 0, label: "ذهب عيار 18" },
    SilverKG: { current: 0, previous: 0, label: "الفضة (كيلو)" },
  });

  const [chartData, setChartData] = useState([]);
  const [countdown, setCountdown] = useState(15);
  const [isUpdating, setIsUpdating] = useState(false);

  const processData = (data) => {
    if (!Array.isArray(data)) return [];
    return data.map((item) => ({
      x: new Date(item.t).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      }),
      y: Number(item.y),
    }));
  };

  const performUpdate = async () => {
    if (isUpdating) return;
    setIsUpdating(true);
    try {
      const response = await fetch(DAR_AL_SABAEK_API);
      const darAlSabaekData = await response.json();

      if (
        darAlSabaekData &&
        darAlSabaekData.isSuccess &&
        darAlSabaekData.result
      ) {
        const gold24Obj = darAlSabaekData.result.find(
          (item) => item.metalType === 1,
        );
        const silverObj = darAlSabaekData.result.find(
          (item) => item.metalType === 2,
        );

        if (gold24Obj) {
          const baseGold24 = Number(gold24Obj.buyPrice24);
          const baseSilverGram = silverObj ? Number(silverObj.buyPrice24) : 0;

          const price24 = baseGold24;
          const price22 = baseGold24 * (22 / 24);
          const price21 = baseGold24 * (21 / 24);
          const price18 = baseGold24 * (18 / 24);
          const priceSilverKG = baseSilverGram * 1000;

          setPrices((prevPrices) => ({
            "24K": {
              current: price24,
              previous: prevPrices["24K"].current || price24,
              label: "عيار 24",
            },
            "22K": {
              current: price22,
              previous: prevPrices["22K"].current || price22,
              label: "عيار 22",
            },
            "21K": {
              current: price21,
              previous: prevPrices["21K"].current || price21,
              label: "عيار 21",
            },
            "18K": {
              current: price18,
              previous: prevPrices["18K"].current || price18,
              label: "عيار 18",
            },
            SilverKG: {
              current: priceSilverKG,
              previous: prevPrices["SilverKG"].current || priceSilverKG,
              label: "الفضة بالكيلو",
            },
          }));

          if (price24 > 0) {
            await fetch(
              "https://sabaekalkhaleej.crystalcodekw.com/history_api.php",
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ y: parseFloat(price24.toFixed(3)) }),
              },
            );

            const resH = await fetch(
              "https://sabaekalkhaleej.crystalcodekw.com/history_api.php",
            );
            const updatedHistory = await resH.json();
            setChartData([
              { id: "Gold24K", data: processData(updatedHistory) },
            ]);
          }
        }
      }
    } catch (error) {
      console.error("خطأ أثناء جلب أسعار دار السبائك:", error);
    } finally {
      setIsUpdating(false);
      setCountdown(15);
    }
  };

  useEffect(() => {
    performUpdateRef.current = performUpdate;
  });

  useEffect(() => {
    performUpdate();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (performUpdateRef.current) {
            performUpdateRef.current();
          }
          return 15;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!tradingViewContainer.current) return;
    const script = document.createElement("script");
    script.src =
      "https://s3.tradingview.com/external-embedding/embed-widget-single-quote.js";
    script.async = true;
    script.innerHTML = JSON.stringify({
      symbol: "TVC:GOLD",
      width: "100%",
      colorTheme: "dark",
      isTransparent: true,
      locale: "ar",
    });
    tradingViewContainer.current.innerHTML = "";
    tradingViewContainer.current.appendChild(script);
  }, []);

  const yValues = (chartData[0]?.data || []).map((d) => d.y);
  let manualTicks = [];
  let minValue = "auto";
  let maxValue = "auto";

  if (yValues.length > 0) {
    const dataMin = Math.min(...yValues);
    const dataMax = Math.max(...yValues);

    let buffer = 0.05;
    if (dataMin !== dataMax) {
      buffer = Math.max((dataMax - dataMin) * 0.5, 0.02);
    }

    minValue = dataMin - buffer;
    maxValue = dataMax + buffer;

    const step = (maxValue - minValue) / 4;
    manualTicks = [
      minValue,
      minValue + step,
      minValue + step * 2,
      minValue + step * 3,
      maxValue,
    ];
  }

  const displayOrder = ["24K", "22K", "21K", "18K", "SilverKG"];

  // ✅ دالة لاختيار الأيقونة المناسبة لكل نوع
  const getCardIcon = (key) => {
    switch (key) {
      case "24K":
        return (
          <WorkspacePremiumOutlinedIcon
            sx={{ color: "#FFD700", fontSize: "30px" }}
          />
        );
      case "22K":
        return (
          <StarsOutlinedIcon sx={{ color: "#FFD700", fontSize: "30px" }} />
        );
      case "21K":
        return (
          <MonetizationOnOutlinedIcon
            sx={{ color: "#FFD700", fontSize: "30px" }}
          />
        );
      case "18K":
        return (
          <DiamondOutlinedIcon sx={{ color: "#FFD700", fontSize: "30px" }} />
        );
      case "SilverKG":
        return (
          <ScaleOutlinedIcon sx={{ color: "#C0C0C0", fontSize: "30px" }} />
        ); // لون فضي وأيقونة ميزان للكيلو
      default:
        return (
          <MonetizationOnOutlinedIcon
            sx={{ color: "#FFD700", fontSize: "30px" }}
          />
        );
    }
  };

  return (
    <Box m="20px" mt="-5px" position="relative">
      <Box mb="15px" display="flex" justifyContent="center">
        <img
          width={isMobileOrTablet ? "220px" : "320px"}
          src="../../assets/logo2.png"
          alt="Logo"
        />
      </Box>

      <Box
        display="grid"
        gridTemplateColumns={{ xs: "1fr", md: "repeat(12, 1fr)" }}
        gap="20px"
      >
        <Box
          gridColumn={{ xs: "span 1", md: "span 8" }}
          backgroundColor={colors.primary[400]}
          borderRadius="12px"
          p="15px"
          boxShadow="0px 6px 20px rgba(0, 0, 0, 0.3)"
          border="1px solid rgba(255, 215, 0, 0.05)"
        >
          <Typography
            sx={{
              textAlign: "center",
              fontSize: isMobileOrTablet ? "1.1rem" : "1.6rem",
              mb: "15px",
              fontWeight: "bold",
              color: "#FFF",
              letterSpacing: "0.5px",
            }}
          >
            {isUpdating
              ? "جاري مزامنة الأسعار الحية..."
              : `تحديث تلقائي مباشر خلال ${countdown} ثانية`}
          </Typography>
          <Box sx={{ height: isMobileOrTablet ? "320px" : "460px" }}>
            {chartData.length > 0 && (
              <LineChart
                data={chartData}
                minValue={minValue}
                maxValue={maxValue}
                manualTicks={manualTicks}
              />
            )}
          </Box>
        </Box>

        <Box
          gridColumn={{ xs: "span 1", md: "span 4" }}
          display="flex"
          flexDirection="column"
          gap="15px"
        >
          <Box
            backgroundColor={colors.primary[400]}
            borderRadius="12px"
            minHeight="130px"
            p="10px"
            boxShadow="0px 6px 20px rgba(0, 0, 0, 0.2)"
          >
            <div ref={tradingViewContainer}></div>
          </Box>
          <Box display="grid" gridTemplateColumns="repeat(2,1fr)" gap="12px">
            {displayOrder.map((key) => (
              <Box
                key={key}
                sx={{
                  gridColumn: key === "SilverKG" ? "span 2" : "span 1",
                  backgroundColor: colors.primary[400],
                  p: "20px",
                  borderRadius: "12px",
                  border: "1px solid rgba(255, 215, 0, 0.08)",
                  boxShadow: "0 4px 25px rgba(0,0,0,0.2)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    borderColor: "rgba(255, 215, 0, 0.3)",
                  },
                }}
              >
                <StatBox
                  title={`${(prices[key].current || 0).toFixed(3)} KD`}
                  subtitle={prices[key].label}
                  priceData={prices[key]}
                  // ✅ استدعاء دالة الأيقونات المخصصة هنا 👇
                  icon={getCardIcon(key)}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default GoldPriceSystem;
